import type { GameLine, GameRole, SessionView } from '../api/rooms'
import type { Line, Role } from '../api/types'
import type { PlayItem } from './order'

/**
 * Превращает реплики сетевой сессии в плейлист для экрана записи.
 *
 * В комнате каждый пишет только свои реплики и не ждёт чужой очереди, поэтому
 * экран передачи устройства здесь не нужен: все играют одновременно
 * (docs/ROOMS.md, «Запись»).
 */
export function buildRoomPlaylist(session: SessionView, participantId: string): PlayItem[] {
  const mine = new Set(session.myLines)
  const roles = new Map(session.game.roles.map((r) => [r.id, r]))
  const lines = session.game.lines
    .filter((l) => mine.has(l.id) && l.end > l.start)
    .sort((a, b) => a.start - b.start || a.end - b.end)

  // Подстраховка: сервер уже отдал только мои реплики, но если состав ролей
  // изменился между запросами, лучше показать пусто, чем чужой текст.
  const myRoles = new Set(
    session.session.assignments.filter((a) => a.participantId === participantId).map((a) => a.roleId),
  )

  return lines
    .filter((l) => myRoles.size === 0 || myRoles.has(l.roleId))
    .map((line, index, all) => {
      const role = roles.get(line.roleId) ?? null
      return {
        line: toLine(line),
        role: role ? toRole(role) : null,
        roleName: role?.name ?? 'Без роли',
        roleColor: role?.color ?? '#888888',
        handoff: false,
        segmentSize: all.length - index,
      }
    })
}

/** Реплика игры и реплика проекта отличаются только происхождением. */
function toLine(line: GameLine): Line {
  return {
    id: line.id,
    projectId: line.gameId,
    roleId: line.roleId,
    start: line.start,
    end: line.end,
    text: line.text,
    textOriginal: line.textOriginal,
  }
}

function toRole(role: GameRole): Role {
  return { id: role.id, projectId: role.gameId, name: role.name, color: role.color }
}
